import { useCallback, useEffect, useState } from 'react';
import { useManagePrivileges } from './useManagePrivileges';
import { Button, Divider, Empty, Modal, Select, Typography } from 'antd';
import Icon from '../../../components/ui-components/Icon';
import GrayChevronDown from '../../../assets/images/gray-chevron-down.svg';
import { displayError } from '../../../shared/Utils';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { Privilege } from '../../../shared/RoleBasedTypes';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useBatchId } from '../../../hooks/useBatchId';
import { MODULES_LIST } from '../../../shared/Routes';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import { FeaturePrivilegeMatrix } from './FeaturePrivilegeMatrix';

const { Text } = Typography;

interface LocationState {
	roleId: number;
	roleCode: string;
	roleName: string;
	modules: {
		id: number;
		name: string;
	}[];
}

export const ManagePrivileges = () => {
	const { t } = useTranslation();
	const { showToastMessage } = useToastMessage();
	const { navigateToRoute } = useBatchId();
	const {
		fetchPrivilegesByRole,
		fetchPrivilegesByModule,
		privilegesByRole,
		privilegesByModule,
		updatePrivileges,
		selectedModuleCode,
	} = useManagePrivileges();
	const location = useLocation();
	const state = location.state as LocationState;
	const roleIdFromState = state?.roleId;
	const roleCodeFromState = state?.roleCode;
	const roleNameFromState = state?.roleName;
	const modulesFromState = state?.modules || [];
	const [moduleForPrivilegeUpdates, setModuleForPrivilegeUpdates] = useState<
		number | null
	>(null);
	const [editablePrivileges, setEditablePrivileges] = useState<Privilege[]>([]);
	const [selectedPrivilegeIds, setSelectedPrivilegeIds] = useState<number[]>(
		[]
	);
	const moduleOptions = modulesFromState?.map((module) => ({
		label: module.name,
		value: module.id,
	}));

	useEffect(() => {
		if (!privilegesByModule?.length) {
			setEditablePrivileges([]);
			setSelectedPrivilegeIds([]);
			return;
		}

		setEditablePrivileges(privilegesByModule);

		const assignedIds = privilegesByRole?.map((rp) => rp.privilege.id) ?? [];

		setSelectedPrivilegeIds(assignedIds);
	}, [privilegesByModule, privilegesByRole]);

	const handleModuleChange = (value: number) => {
		setModuleForPrivilegeUpdates(value);
	};

	const handleGetPrivileges = () => {
		if (!roleCodeFromState || !moduleForPrivilegeUpdates) {
			return;
		}

		fetchPrivilegesByRole(roleCodeFromState, moduleForPrivilegeUpdates).catch(
			(e) => {
				const message = displayError(e);
				showToastMessage('error', message);
			}
		);

		fetchPrivilegesByModule(moduleForPrivilegeUpdates).catch((e) => {
			const message = displayError(e);
			showToastMessage('error', message);
		});
	};

	const handleUpdatePrivileges = () => {
		if (!moduleForPrivilegeUpdates) {
			return;
		}

		updatePrivileges(
			roleIdFromState,
			moduleForPrivilegeUpdates,
			selectedPrivilegeIds
		)
			.then(() => {
				Modal.success({
					title: 'Privileges Updated',
					content: 'Privileges have been successfully updated.',
					centered: true,
					okText: 'OK',
					okButtonProps: {
						type: 'primary',
						className: 'bg-primary-blue',
					},
					onOk() {
						navigateToRoute(MODULES_LIST);
					},
				});
			})
			.catch((e) => {
				const message = displayError(e);
				showToastMessage('error', message);
			});
	};

	const handleBackClick = useCallback(() => {
		navigateToRoute(MODULES_LIST);
	}, [navigateToRoute]);

	return (
		<div className="p-8 bg-gray-100 min-h-screen">
			<div className="max-w-12xl mx-auto">
				<div className="bg-white rounded-xl shadow-sm border border-solid border-gray-200 p-8">
					<div className="flex gap-6 items-center">
						<Button
							type="primary"
							className="hidden md:block font-bold text-sm mt-5"
							onClick={handleBackClick}
						>
							<span className="flex gap-2">
								<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
								{t('back')}
							</span>
						</Button>
						{/* Card Title */}
						<Text className="font-sans font-bold text-2xl text-primary-blue mt-4">
							{t('managePrivileges')}
						</Text>
					</div>

					{/* Selection Section */}
					<div className="flex flex-col md:flex-row gap-6 items-end mt-8">
						<div className="flex w-[300px] items-center py-1">
							<Text className="text-gray-500 text-lg">Role:</Text>&nbsp;
							<Text className="font-sans font-bold text-lg">
								{roleNameFromState}
							</Text>
						</div>

						<div className="w-[300px]">
							<Select
								placeholder="Select a module"
								options={moduleOptions}
								value={moduleForPrivilegeUpdates}
								onChange={handleModuleChange}
								className="w-full"
								showSearch
								optionFilterProp="label"
								size="large"
								suffixIcon={
									<Icon src={GrayChevronDown} width={18} height={18} />
								}
							/>
						</div>

						<Button
							type="primary"
							size="large"
							onClick={handleGetPrivileges}
							disabled={!roleCodeFromState || !moduleForPrivilegeUpdates}
						>
							{t('getPrivileges')}
						</Button>
					</div>
					<Divider className="my-8 border border-solid border-gray-200" />
					{editablePrivileges.length === 0 ? (
						<div className="mt-10 flex justify-center">
							<Empty
								description={
									moduleForPrivilegeUpdates
										? 'No privileges available for this module.'
										: 'Please select a module from the dropdown and click "Get Privileges" to see privileges.'
								}
							/>
						</div>
					) : (
						<>
							<FeaturePrivilegeMatrix
								privileges={editablePrivileges}
								selectedPrivilegeIds={selectedPrivilegeIds}
								onChange={setSelectedPrivilegeIds}
								moduleCode={selectedModuleCode}
								roleCode={roleCodeFromState}
							/>
							<div className="mt-8">
								<Button
									type="primary"
									size="large"
									onClick={handleUpdatePrivileges}
									disabled={
										!editablePrivileges.length ||
										!roleCodeFromState ||
										!moduleForPrivilegeUpdates
									}
								>
									{t('updatePrivileges')}
								</Button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};
